import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'

import { ReactComponent as ChatsIcon } from '@app/assets/Small/Outline/Chats.svg'
import { ReactComponent as ClipboardIcon } from '@app/assets/Small/Outline/Clipboard.svg'
import { ReactComponent as InvoiceIcon } from '@app/assets/Small/Outline/Invoice.svg'
import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { useIntl } from '@app/intl/Intl'
import { bemJoin } from '@app/lib/bem'
import { Collapsible } from '@app/lib/display/Collapsible'
import { DropdownItem, DropdownMenu } from '@app/lib/inputs/DropdownMenu'
import { ConsultationTypeTabs } from '@app/lib/layout/ConsultationTypeTabs/ConsultationTypeTabs'
import { PortalContentLayout } from '@app/lib/layout/PortalContentLayout/PortalContentLayout'
import { CourtCaseTabNames, getCourtCasePath } from '@app/modules/CourtCases'
import { useCourtCaseContext } from '@app/modules/CourtCases/Components/CourtCaseContext/CourtCaseContext'
import { CourtCaseSelector } from '@app/modules/CourtCases/Components/CourtCaseSelector/CourtCaseSelector'
import { CourtCaseTabs } from '@app/modules/CourtCases/Components/CourtCaseTabs/CourtCaseTabs'
import ProfileBox from '@app/modules/Messages/Components/ProfileBox/ProfileBox'
import { getSalutation } from '@app/modules/Messages/utils/gender'
import { useMobile } from '@app/services/useMobile'

import { CourtCaseDocuments } from '../CourtCaseDocuments/CourtCaseDocuments'

import CloseDetailsButton from './CloseDetailsButton'
import { CourtCaseStatusButton } from './CourtCaseStatusButton/CourtCaseStatusButton'

import './styles.scss'

export type CourtCaseHeaderProps = {
  activeTab: CourtCaseTabNames
}

export const CourtCaseHeader = ({ activeTab }: CourtCaseHeaderProps) => {
  const { formatMessage } = useIntl()
  const b = bemJoin('court-case-header')
  const history = useHistory()
  const { id, courtCaseUuid } = useParams<{ id: string; courtCaseUuid: string }>()
  const customerId = Number(id)
  const { isMobile } = useMobile()
  const { data, loading } = useCourtCaseContext()
  const [showDetails, setShowDetails] = useState(false)
  const isCourtCaseInvoicingEnabled = useSelector(isFeatureEnabled(Features.COURT_CASE_INVOICING))

  const customer = data?.customer
  const product = data?.customer.membership?.product

  const menuItems: DropdownItem[] = [
    {
      key: 'show-details',
      label: formatMessage({ id: 'CourtCases.Menu.Item.ShowDetails' }),
      leading: <ClipboardIcon />,
      onClick: () => {
        setShowDetails(true)
      }
    },
    {
      key: 'chat-chronic',
      label: formatMessage({ id: 'CourtCases.Menu.Item.ChatChronic' }),
      leading: <ChatsIcon />,
      onClick: () => {
        history.push(getCourtCasePath(customerId, courtCaseUuid, CourtCaseTabNames.ChatChronic))
      }
    },
    !!isCourtCaseInvoicingEnabled && {
      key: 'invoicing',
      label: formatMessage({ id: 'CourtCases.Menu.Item.Invoicing' }),
      leading: <InvoiceIcon />,
      onClick: () => {
        history.push(getCourtCasePath(customerId, courtCaseUuid, CourtCaseTabNames.Invoicing))
      }
    }
  ].filter(Boolean) as DropdownItem[]

  return (
    <PortalContentLayout.TopPane>
      {!loading && customer && product && (
        <Collapsible
          title={[getSalutation(customer.gender), customer.firstName, customer.lastName].join(' ')}
          anchor={'top'}
          color={'neutral'}
          size={'lg'}
          className={b('collapsible')}
        >
          <ProfileBox
            customer={customer}
            latestPurchase={product.name}
            upgradesTo={product.upgradesTo}
          />
        </Collapsible>
      )}
      <ConsultationTypeTabs selected={'CourtCase'} className={b('consultation-type-tabs')} />
      <PortalContentLayout.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <PortalContentLayout.Header.Heading>
            {formatMessage({ id: 'CourtCases.Title' })}
          </PortalContentLayout.Header.Heading>
          <div className={b('menu-container')}>
            <div className={b('selector-row')}>
              <CourtCaseSelector />
              <DropdownMenu items={menuItems} align={'right'} className={b('additional-options')} />
            </div>
            <CourtCaseStatusButton isOngoing={data?.ongoing} />
          </div>
        </div>
        <div className={b('details', { ['visible']: showDetails || !isMobile })}>
          <CourtCaseDocuments />
          {isMobile ? <CloseDetailsButton setShowDetails={setShowDetails} /> : null}
        </div>
      </PortalContentLayout.Header>
      <div className={b('tabs')}>
        <CourtCaseTabs selected={activeTab} />
      </div>
    </PortalContentLayout.TopPane>
  )
}
